import React, { useEffect, useMemo, useState } from 'react';
import {
  AlignLeftOutlined,
  AlignRightOutlined,
  CaretDownFilled,
  CaretRightFilled,
  UserOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Dropdown, Layout, Menu, notification, Typography } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { logoutRequest } from '../services/authRequest';
import { setLogout, setSessionRefresh } from '../store/ducks/user/userSlice';
import { getListPartnersRequest } from '../services/onlinestore/partnerServiceRequest';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import { LogoutIcon } from '../assets/icon/iconSvg';
import {
  RiStore2Line,
  RiLayoutGridLine,
  RiLayoutRowLine,
  RiApps2Line,
  RiPriceTag3Line,
  RiUserFollowLine,
  RiShoppingCart2Line,
  RiExchangeFundsLine,
  RiVideoLine,
  RiMovie2Line,
  RiMapPinRangeLine,
  RiGlobalLine,
  RiArticleLine,
  RiStackLine,
  RiUserLine,
  RiShieldLine,
  RiShieldUserLine,
} from 'react-icons/ri';


dayjs.locale('vi');

const { Sider, Content } = Layout;

const AuthLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const dataRedux = useSelector((state) => state.user);
  const [selectKeys, setSelectKeys] = useState([]);
  useEffect(() => {
    setSelectKeys([location.pathname]);
  }, [location]);

  // Đồng bộ Redux khi axiosConfig refresh session thành công
  useEffect(() => {
    const handleSessionRefresh = (e) => dispatch(setSessionRefresh(e.detail));
    window.addEventListener('session-refreshed', handleSessionRefresh);
    return () => window.removeEventListener('session-refreshed', handleSessionRefresh);
  }, [dispatch]);

  const { data: dataPartners } = useQuery({
    queryKey: ['dataPartners'],
    queryFn: () => getListPartnersRequest({ page: 1, limit: 1000, status: [1, 2] }),
  });

  const { mutate: mutateLogout } = useMutation({
    mutationFn: logoutRequest,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        await dispatch(setLogout());
        notification.success({ message: 'Đăng xuất thành công!' });
        navigate('/login');
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const FULL_ACCESS_ROLES = [1, 9999];
  const userPermissions = (dataRedux?.info?.permissions || []).map((item) => item.key_menu);
  const isFullAccess = FULL_ACCESS_ROLES.includes(dataRedux?.info?.role);

  const items = useMemo(() => {
    // permKey = key_menu từ API permission, dùng để lọc quyền
    const menuLink = (label, path, icon, permKey) => ({
      key: path,
      label: <Link to={path}>{label}</Link>,
      icon,
      permKey,
    });

    // Lọc children theo permissions của user
    const filterByPermission = (children) => {
      if (isFullAccess) return children;
      return children?.filter((item) => {
        // Check permKey trước - nếu có permKey mà user không có quyền → ẩn luôn
        if (item.permKey) {
          const keys = Array.isArray(item.permKey) ? item.permKey : [item.permKey];
          if (!keys.some((k) => userPermissions.includes(k))) return false;
        }
        // Sub-group (có children) → lọc đệ quy
        if (item.children) {
          item.children = filterByPermission(item.children);
          return item.children?.length > 0;
        }
        return true;
      });
    };

    const allMenus = [
      // Item độc lập — ai cũng thấy, không cần permission
      menuLink('Thông tin tài khoản', '/info', <UserOutlined />),
      {
        key: 'cms-online-store',
        label: 'Online Store',
        icon: <RiStore2Line size={20} />,
        children: [
          menuLink('Quản lý khối', '/online-store/quan-ly-khoi', <RiLayoutGridLine size={18} />, '/quan-ly-khoi'),
          menuLink(
            'Quản lý item khối',
            '/online-store/quan-ly-item-khoi',
            <RiLayoutRowLine size={18} />,
            '/quan-ly-item-khoi'
          ),
          menuLink(
            'Quản lý MiniApps',
            '/online-store/quan-ly-mini-apps',
            <RiApps2Line size={18} />,
            '/quan-ly-mini-apps'
          ),
          menuLink('Quản lý Tags', '/online-store/quan-ly-tags', <RiPriceTag3Line size={18} />, '/quan-ly-tags'),
          menuLink(
            'Quản lý đối tác',
            '/online-store/quan-ly-doi-tac',
            <RiUserFollowLine size={18} />,
            '/quan-ly-doi-tac'
          ),
          // menuLink('Quản lý đơn hàng', '/', <RiShoppingCart2Line size={18} />, '/quan-ly-don-hang'),
          {
            key: 'group-reconcile',
            label: 'Đối soát',
            icon: <RiExchangeFundsLine size={18} />,
            permKey: '/reconcile',
            children: dataPartners?.data
              ?.filter((item) => item?.status === 1 || item?.status === 2)
              ?.map((item) => menuLink(item?.title, `/online-store/reconcile/${item?.id}`)),
          },
        ],
      },
      {
        key: 'lv-video',
        label: 'LV Video',
        icon: <RiVideoLine size={20} />,
        children: [
          menuLink('Quản lý video', '/lv-video/quan-ly-video', <RiMovie2Line size={18} />, '/quan-ly-video'),
          menuLink(
            'Quản lý khối video',
            '/lv-video/quan-ly-khoi-video',
            <RiLayoutGridLine size={18} />,
            '/quan-ly-khoi-video'
          ),
          menuLink(
            'Quản lý tag video',
            '/lv-video/quan-ly-tag-video',
            <RiPriceTag3Line size={18} />,
            '/quan-ly-tag-video'
          ),
          menuLink('Quản lý vị trí', '/lv-video/quan-ly-vi-tri', <RiMapPinRangeLine size={18} />, '/quan-ly-vi-tri'),
        ],
      },
      {
        key: 'lichviet-web',
        label: 'Lịch Việt Web',
        icon: <RiGlobalLine size={20} />,
        children: [
          menuLink(
            'Quản lý danh mục',
            '/lichviet-web/quan-ly-danh-muc',
            <RiStackLine size={18} />,
            '/quan-ly-danh-muc'
          ),
          menuLink(
            'Quản lý bài viết',
            '/lichviet-web/quan-ly-bai-viet',
            <RiArticleLine size={18} />,
            '/quan-ly-bai-viet'
          ),
        ],
      },
      {
        key: 'cms-premium',
        label: 'Quản lý dịch vụ',
        icon: <RiShoppingCart2Line size={20} />,
        children: [
          menuLink('Dịch vụ', '/cms/quan-ly-dich-vu', <RiApps2Line size={18} />, '/quan-ly-dich-vu'),
          menuLink('Landing Page', '/cms/quan-ly-service-ldp', <RiLayoutRowLine size={18} />, '/quan-ly-service-ldp'),
          menuLink('Tính năng', '/cms/quan-ly-tinh-nang', <RiPriceTag3Line size={18} />, '/quan-ly-tinh-nang'),
          menuLink('Gói Premium', '/cms/quan-ly-goi-premium', <RiStore2Line size={18} />, '/quan-ly-goi-premium'),
          menuLink('Đơn hàng', '/cms/quan-ly-don-hang', <RiShoppingCart2Line size={18} />, '/quan-ly-don-hang'),
          menuLink(
            'Lịch sử GD',
            '/cms/quan-ly-history-premium',
            <RiExchangeFundsLine size={18} />,
            '/quan-ly-history-premium'
          ),
          menuLink('MB push BDSD', '/cms/mb-push-bdsd', <RiExchangeFundsLine size={18} />, '/mb-push-bdsd'),
          menuLink(
            'Store Notification',
            '/cms/store-notifications',
            <RiExchangeFundsLine size={18} />,
            '/store-notifications'
          ),
        ],
      },
      {
        key: 'cms-next-v4',
        label: 'CMS Next V4',
        icon: <RiApps2Line size={20} />,
        children: [
          menuLink('App Version', '/cms/next/quan-ly-app-version', <RiLayoutRowLine size={18} />, '/quan-ly-app-version'),
          menuLink('API Version', '/cms/next/quan-ly-version-api', <RiLayoutRowLine size={18} />, '/quan-ly-version-api'),
          menuLink('Đánh giá App', '/cms/next/danh-gia-ung-dung', <RiArticleLine size={18} />, '/danh-gia-ung-dung'),
          menuLink('Đánh giá DV', '/cms/next/danh-gia-dich-vu', <RiArticleLine size={18} />, '/danh-gia-dich-vu'),
          menuLink('Quản lý cấu hình', '/cms/next/quan-ly-cau-hinh', <RiLayoutRowLine size={18} />, '/quan-ly-cau-hinh'),
          menuLink('Quản lý tiện ích', '/cms/next/quan-ly-tien-ich', <RiLayoutRowLine size={18} />, '/quan-ly-tien-ich'),
          menuLink('Chiến dịch', '/cms/next/quan-ly-chien-dich', <RiPriceTag3Line size={18} />, '/quan-ly-chien-dich'),
          menuLink('Mã giảm giá', '/cms/next/quan-ly-ma-giam-gia', <RiPriceTag3Line size={18} />, '/quan-ly-ma-giam-gia'),
          menuLink('Cấu hình SKTT', '/cms/next/quan-ly-cau-hinh-sktt', <RiLayoutRowLine size={18} />, '/quan-ly-cau-hinh-sktt'),
          menuLink('Khối dịch vụ', '/cms/next/quan-ly-khoi-dich-vu', <RiApps2Line size={18} />, '/quan-ly-khoi-dich-vu'),
          menuLink('Nội dung truyền thông', '/cms/next/quan-ly-noi-dung-truyen-thong', <RiArticleLine size={18} />, '/quan-ly-noi-dung-truyen-thong'),
        ],
      },
      {
        key: 'user-management',
        label: 'Quản lý User',
        icon: <RiUserLine size={20} />,
        children: [
          menuLink(
            'Tài khoản người dùng',
            '/cms/quan-ly-user',
            <RiUserLine size={18} />,
            '/quan-ly-user'
          ),
          menuLink(
            'Thành viên gia đình',
            '/cms/quan-ly-thanh-vien-gia-dinh',
            <RiUserFollowLine size={18} />,
            '/quan-ly-thanh-vien-gia-dinh'
          ),
          menuLink('Premium', '/cms/quan-ly-premium', <RiExchangeFundsLine size={18} />, '/quan-ly-premium'),
          menuLink(
            'User Feature',
            '/cms/quan-ly-user-feature',
            <RiUserFollowLine size={18} />,
            '/quan-ly-user-feature'
          ),
        ],
      },
      {
        key: 'object-event',
        label: 'Object & Event',
        icon: <RiGlobalLine size={20} />,
        children: [
          menuLink(
            'Thể loại sự kiện',
            '/cms/quan-ly-the-loai-su-kien',
            <RiPriceTag3Line size={18} />,
            '/quan-ly-the-loai-su-kien'
          ),
          menuLink(
            'Danh mục sự kiện',
            '/cms/quan-ly-danh-muc-su-kien',
            <RiPriceTag3Line size={18} />,
            '/quan-ly-danh-muc-su-kien'
          ),
          menuLink(
            'Sự kiện hệ thống',
            '/cms/quan-ly-su-kien-he-thong',
            <RiPriceTag3Line size={18} />,
            '/quan-ly-su-kien-he-thong'
          ),
          // Ẩn menu Sự kiện người dùng theo yêu cầu (route vẫn giữ, chỉ ẩn khỏi menu)
          // menuLink(
          //   'Sự kiện người dùng',
          //   '/cms/quan-ly-su-kien-nguoi-dung',
          //   <RiPriceTag3Line size={18} />,
          //   '/quan-ly-su-kien-nguoi-dung'
          // ),
          menuLink(
            'Loại đối tượng',
            '/cms/quan-ly-loai-doi-tuong',
            <RiPriceTag3Line size={18} />,
            '/quan-ly-loai-doi-tuong'
          ),
          menuLink('Đối tượng', '/cms/quan-ly-doi-tuong', <RiPriceTag3Line size={18} />, '/quan-ly-doi-tuong'),
        ],
      },
      {
        key: 'content-management',
        label: 'Quản trị nội dung',
        icon: <RiArticleLine size={20} />,
        children: [
          menuLink(
            'Danh mục nội dung',
            '/cms/quan-ly-danh-muc-noi-dung',
            <RiStackLine size={18} />,
            '/quan-ly-danh-muc-noi-dung'
          ),
          menuLink('Bài viết', '/cms/quan-ly-bai-viet-nd', <RiArticleLine size={18} />, '/quan-ly-bai-viet-nd'),
          menuLink('Albums', '/cms/quan-ly-albums', <RiLayoutGridLine size={18} />, '/quan-ly-albums'),
          menuLink('Hình ảnh', '/cms/quan-ly-hinh-anh', <RiLayoutGridLine size={18} />, '/quan-ly-hinh-anh'),
          menuLink('Video', '/cms/quan-ly-video-nd', <RiMovie2Line size={18} />, '/quan-ly-video-nd'),
          menuLink('Quản lý File', '/cms/quan-ly-file', <RiLayoutGridLine size={18} />, '/quan-ly-file'),
          menuLink('Danh ngôn', '/cms/quan-ly-danh-ngon', <RiLayoutGridLine size={18} />, '/quan-ly-danh-ngon'),
          menuLink(
            'Cung hoàng đạo',
            '/cms/quan-ly-cung-hoang-dao',
            <RiLayoutGridLine size={18} />,
            '/quan-ly-cung-hoang-dao'
          ),
          menuLink('Tử vi', '/cms/quan-ly-tu-vi', <RiLayoutGridLine size={18} />, '/quan-ly-tu-vi'),
          // menuLink('Thiệp', '/cms/quan-ly-thiep', <RiLayoutGridLine size={18} />, '/quan-ly-thiep'),
          menuLink('Tính năng', '/cms/quan-ly-tinh-nang', <RiPriceTag3Line size={18} />, '/quan-ly-tinh-nang'),
          menuLink('Khảo sát (Survey)', '/survey/management', <RiArticleLine size={18} />, '/survey-management'),
          menuLink(
            'Khảo sát tính năng hữu ích',
            '/survey/feature-rating-config',
            <RiArticleLine size={18} />,
            ['/feature-rating-config', '/survey-management']
          ),
          menuLink(
            'Danh mục tính năng góp ý',
            '/survey/feedback-feature-config',
            <RiPriceTag3Line size={18} />,
            '/feedback-feature-config'
          ),
          menuLink('Góp ý người dùng', '/survey/feedbacks', <RiArticleLine size={18} />, '/survey-feedbacks'),
        ],
      },
      {
        key: 'admin',
        label: 'Quản trị hệ thống',
        icon: <RiShieldUserLine size={20} />,
        children: [
          menuLink('Quản lý tài khoản', '/admin/account-management', <RiUserLine size={18} />, '/account-management'),
          menuLink('Quản lý vai trò', '/admin/role-management', <RiShieldLine size={18} />, '/role-management'),
        ],
      },
    ];

    return allMenus
      .map((group) => {
        if (!group.children) return group;
        return { ...group, children: filterByPermission(group.children) };
      })
      .filter((group) => !group.children || group.children.length > 0);
  }, [dataPartners, isFullAccess, userPermissions]);


  return (
    <Layout>
      <Sider
        className="fixed top-0 left-0 h-screen overflow-hidden bg-[#075CDD] z-20 transition-all duration-300 ease-in-out"
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={256}
        collapsedWidth={80}
      >
        <div className="flex justify-between items-center w-full h-16 px-6 py-4 bg-[#0C2FDD] transition-all duration-300">
          <div 
            className={`text-2xl text-white font-semibold overflow-hidden transition-all duration-300 whitespace-nowrap ${
              collapsed ? 'w-0 opacity-0' : 'w-full opacity-100'
            }`}
          >
            LV CMS 4.0
          </div>
          <Button
            type="text"
            className="flex items-center justify-center p-0"
            icon={
              collapsed ? (
                <AlignLeftOutlined className="text-white text-2xl" />
              ) : (
                <AlignRightOutlined className="text-white text-2xl" />
              )
            }
            onClick={() => setCollapsed(!collapsed)}
          />
        </div>
        <Menu
          mode="inline"
          theme="dark"
          inlineCollapsed={collapsed}
          expandIcon={({ isOpen }) => (isOpen ? <CaretDownFilled /> : <CaretRightFilled />)}
          className="bg-[#075CDD] gap-4 overflow-y-auto scrollbar-none border-none py-3"
          selectedKeys={selectKeys}
          items={items}
        />
        <div
          onClick={() => mutateLogout()}
          className="absolute bottom-0 left-0 flex justify-start items-center w-full h-14 px-6 py-3 bg-[#0C2FDD] hover:bg-blue-800 z-10 hover:cursor-pointer transition-all duration-300 gap-3 group"
        >
          <div className="flex-shrink-0">
            <LogoutIcon color="white" />
          </div>
          <div 
            className={`text-white text-sm font-semibold transition-all duration-300 whitespace-nowrap overflow-hidden ${
              collapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
            }`}
          >
            Đăng xuất
          </div>
        </div>
      </Sider>
      <Layout 
        className="min-h-screen transition-all duration-300 ease-in-out" 
        style={{ marginLeft: collapsed ? 80 : 256 }}
      >
        <div className="sticky top-0 z-10 bg-white h-16">
          <div className="flex flex-row w-full h-full justify-between items-center px-6">
            <div className="flex flex-col h-full justify-between items-start py-2">
              <div className="text-[#3699FF] text-base font-semibold">{dayjs().format('DD/MM/YYYY')}</div>
              <div className="text-[#181C32] text-sm font-semibold">{dayjs().format('dddd')}</div>
            </div>
            <div className="w-full flex justify-end items-center">
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'info',
                      icon: <UserOutlined />,
                      label: <Link to="/info">Thông tin tài khoản</Link>,
                    },
                    { type: 'divider' },
                    {
                      key: 'logout',
                      icon: <LogoutIcon color="black" />,
                      label: <span className="ml-3">Đăng xuất</span>,
                      onClick: () => mutateLogout(),
                    },
                  ],
                }}
                placement="bottom"
                overlayClassName={'shadow'}
              >
                <div className={'cursor-pointer inline-flex items-center gap-1'}>
                  <Typography.Text className={' font-medium mr-1 text-base inline-flex items-center'}>
                    {`${dataRedux?.info?.username}`}
                  </Typography.Text>
                  <Avatar src={dataRedux.info.avatar} icon={<UserOutlined />} size={40} />
                </div>
              </Dropdown>
            </div>
          </div>
        </div>
        <Content className="mx-6 my-4 p-6 min-h-72 bg-white rounded-md">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default AuthLayout;
